// @flow
import React from 'react';
import dataFormat from 'date-fns/format';
import { camelCase } from 'lodash';
import { imageURL, numberWithCommas, orderShippingStatus } from 'lib/common';
import Button from 'components/common/Button';
import OrderSearch from './OrderSearch';
import './OrderListTemplate.scss';

type Props = {
  orders: any,
  onClick: any,
  downloadRequestsExcel: any,
  downloadInventoriesExcel: any,
  onSearchInputChange: (searchTerm: string) => void,
  onSearchSubmit: () => void,
  searchTerm: string,
  isSearching: boolean,
  onAddToCart: (orderId: string) => void,
};

const statusTrans = key => {
  let words = {
    statusPlaced: '결제 대기',
    statusCancelledByStore: '운영자 취소',
    statusCancelledByCustomer: '고객 취소',
    statusPaid: '결제 완료',
    statusUnderPaid: '부분 결제',
    statusOverPaid: '초과 결제',
    statusRefunded: '환불 완료',
    statusPartiallyRefunded: '부분 환불 완료',
    statusUnderRefunded: '환불 처리 중',
    statusOverRefunded: '초과 환불',
    shippingStatusPending: '배송 대기',
    shippingStatusShipped: '배송중',
    shippingStatusArrived: '배송 완료',
    shippingStatusReceived: '수령 완료',
  };
  return words[key];
};

const OrderListTemplate = (props: Props) => (
  <div className="OrderListTemplate">
    {/* 헤더 섹션 */}
    <div className="order-header-section">
      <div className="order-header-content">
        <div className="order-title-area">
          <h1 className="order-title">
            <button
              className="back-button"
              onClick={() => window.history.back()}
              aria-label="뒤로가기"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            주문 목록
          </h1>
        </div>
        <div className="order-actions">
          <Button
            theme="default"
            color="green"
            onClick={props.downloadRequestsExcel}
            className="export-btn"
          >
            <span role="img" aria-label="다운로드" className="btn-icon">
              📊
            </span>
            구매관리 엑셀
          </Button>
          <Button
            theme="default"
            color="green"
            onClick={props.downloadInventoriesExcel}
            className="export-btn"
          >
            <span role="img" aria-label="다운로드" className="btn-icon">
              📈
            </span>
            재고관리 엑셀
          </Button>
        </div>
      </div>
    </div>

    {/* 검색 섹션 */}
    <OrderSearch
      searchTerm={props.searchTerm}
      onSearchInputChange={props.onSearchInputChange}
      onSearchSubmit={props.onSearchSubmit}
      isSearching={props.isSearching}
    />

    {/* 주문 목록 카드 */}
    <div className="order-list-card">
      {props.orders.items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <span role="img" aria-label="빈 주문목록">
              📦
            </span>
          </div>
          <h3 className="empty-title">주문 내역이 없습니다</h3>
          <p className="empty-description">
            아직 주문한 상품이 없습니다.
            <br />
            상품을 주문하면 여기에 표시됩니다.
          </p>
        </div>
      ) : (
        <div className="order-list-content">
          <div className="order-table-wrapper">
            <table className="order-table">
              <thead>
                <tr>
                  <th>주문 정보</th>
                  <th>상품 정보</th>
                  <th>주문 금액</th>
                  <th>주문 일시</th>
                  <th>액션</th>
                </tr>
              </thead>
              <tbody>
                {props.orders.items.map((order, index) => (
                  <tr className="order-row" key={order.id}>
                    <td className="order-info-cell">
                      <div className="order-id-section">
                        <span
                          className="order-id-link"
                          onClick={() => props.onClick(`/orders/${order.id}`)}
                        >
                          {order.id}
                        </span>
                      </div>
                      <div className="order-status-section">
                        <span className="status-badge order-status" data-status={order.status}>
                          {order.status === 'cancelled'
                            ? statusTrans(
                                camelCase(['status', order.status, 'by', order.cancellation.by]),
                              )
                            : statusTrans(camelCase(['status', order.status]))}
                        </span>
                        {orderShippingStatus(order) && (
                          <span
                            className="status-badge shipping-status"
                            data-status={orderShippingStatus(order)}
                          >
                            {statusTrans(camelCase(['shippingStatus', orderShippingStatus(order)]))}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="product-info-cell">
                      <div
                        className="product-info"
                        onClick={() => props.onClick(`/orders/${order.id}`)}
                      >
                        <div className="product-thumbnail">
                          <img
                            src={imageURL(order.items[0].product.thumbnail, 120, 120)}
                            alt={order.items[0].product.name}
                          />
                        </div>
                        <div className="product-details">
                          <span className="product-name">{order.items[0].product.name}</span>
                          {order.items.length > 1 && (
                            <span className="additional-items">
                              외 {order.items.length - 1}개의 상품
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="amount-cell">
                      <span className="order-amount">
                        {numberWithCommas(order.total.amount.raw)}원
                      </span>
                    </td>

                    <td className="date-cell">
                      <span className="order-date">
                        {dataFormat(order.created_at, 'YYYY년 M월 D일')}
                      </span>
                      {/* <span className="order-time">
                        {dataFormat(order.created_at, 'HH:mm')}
                      </span> */}
                    </td>

                    <td className="action-cell">
                      <div className="action-buttons">
                        <button
                          type="button"
                          onClick={() => props.onClick(`/orders/${order.id}/invoice`)}
                          className="invoice-btn"
                        >
                          <span role="img" aria-label="문서" className="btn-icon">
                            📄
                          </span>
                          거래명세서
                        </button>
                        <button
                          type="button"
                          onClick={() => props.onAddToCart(order.id)}
                          className="add-to-cart-btn"
                        >
                          <span role="img" aria-label="장바구니" className="btn-icon">
                            🛒
                          </span>
                          장바구니 담기
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  </div>
);

export default OrderListTemplate;



// WEBPACK FOOTER //
// ./src/components/order/OrderListTemplate/OrderListTemplate.js